import CloseIcon from '@mui/icons-material/Close';
import ControlPointIcon from '@mui/icons-material/ControlPoint';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  FormControl,
  IconButton,
  InputAdornment,
  OutlinedInput,
  TextField,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { formatDecompositionResponse } from '../../helpers/utils/formatDecompositionResponse';
import useCheckDecomposition from '../../hooks/products/useCheckDecomposition';

const LogisticDrawer = ({ open, onClose }) => {
  const [line, setLine] = useState('');
  const [lines, setLines] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [decompositionUi, setDecompositionUi] = useState(null);
  const { checkDecomposition } = useCheckDecomposition();

  useEffect(() => {
    if (open) {
      setLines([]);
      setLine('');
      setDecompositionUi(null);
    }
  }, [open]);

  const handleAddItem = () => {
    const trimmed = line.trim();
    let newLines = [
      {
        id: uuidv4(),
        internalProduct: trimmed,
        reference: trimmed,
        quantity: 1,
      },
      ...(lines || []),
    ];

    const existing = lines?.find((obj) => obj.internalProduct === trimmed);
    const index = lines?.indexOf(existing);

    if (lines[index] && lines[index].quantity + 1 <= 10) {
      newLines = [
        {
          ...lines[index],
          quantity: lines[index].quantity + 1,
        },
        ...lines.filter((item) => item.id !== lines[index].id),
      ];
    }

    setLines(newLines);
    setLine('');
  };

  const handleRemoveItem = (lineId) => {
    setLines((prev) => prev?.filter((l) => l.id !== lineId));
  };

  const handleChangeQuantity = (value, id) => {
    setLines((prev) =>
      prev?.map((l) => {
        if (l.id !== id) return l;
        if (value === '') {
          return { ...l, quantity: 1 };
        }
        const numValue = parseInt(value, 10);
        if (Number.isNaN(numValue)) return l;
        const constrainedValue = Math.min(Math.max(numValue, 1), 500);
        return { ...l, quantity: constrainedValue };
      })
    );
  };

  const buildLinesPayload = () => {
    return (lines || [])
      .map((lineItem) => {
        const reference =
          lineItem.reference?.trim() || lineItem.internalProduct?.trim() || '';
        return {
          reference,
          quantity: lineItem.quantity ?? 1,
        };
      })
      .filter((item) => item.reference);
  };

  const handleSubmit = async () => {
    const linesPayload = buildLinesPayload();
    if (linesPayload.length === 0) {
      return;
    }
    const orderedRefs = linesPayload.map((item) => item.reference);
    try {
      setIsSubmitting(true);
      setDecompositionUi(null);
      const response = await checkDecomposition(linesPayload);
      setDecompositionUi(formatDecompositionResponse(response, orderedRefs));
    } catch (error) {
      console.error('Validation décomposition :', error);
      setDecompositionUi(formatDecompositionResponse(null, orderedRefs));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        '& .MuiDrawer-paper': {
          width: '50%',
        },
      }}
    >
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'start',
            alignItems: 'center',
            alignContent: 'center',
            padding: '8px',
          }}
        >
          <IconButton onClick={onClose} sx={{ marginRight: '16px' }}>
            <CloseIcon />
          </IconButton>
          <Typography variant="h6" component="div">
            Décomposition logistique
          </Typography>
        </Box>
        <Divider />
        <Box sx={{ padding: '16px', flex: 1, overflow: 'auto' }}>
          <Typography variant="subtitle2" color="primary" gutterBottom>
            Pièces / colis
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, mb: 2 }}>
            <FormControl
              sx={{ width: '510px', maxWidth: '100%' }}
              variant="outlined"
            >
              <OutlinedInput
                id="logistic-reference-piece"
                placeholder="Référence de la pièce ou du colis"
                type="text"
                size="small"
                onChange={(e) => setLine(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && line?.trim().length === 10) {
                    e.preventDefault();
                    handleAddItem();
                  }
                }}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="ajouter une pièce ou un colis"
                      onClick={handleAddItem}
                      edge="end"
                      disabled={line?.length !== 10}
                    >
                      <ControlPointIcon />
                    </IconButton>
                  </InputAdornment>
                }
                value={line}
              />
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 0.5, display: 'block' }}
              >
                Exemple de références valides : COM-000001, PCS-004309,
                STI-000357, ACC-000143
              </Typography>
            </FormControl>
          </Box>

          {lines?.length === 0 ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                gap: 2,
                mb: 2,
                mt: 1,
                p: 1,
                border: '1px solid rgb(19 64 123 / 24%)',
                color: 'rgb(19 64 123 / 44%)',
                maxWidth: '510px',
                borderRadius: '4px',
              }}
            >
              <Typography>Aucun produit</Typography>
            </Box>
          ) : (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                mt: 2,
                mb: 2,
              }}
            >
              {lines?.map((lineItem) => (
                <Box
                  key={lineItem.id}
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    flexWrap: 'wrap',
                    gap: 1,
                  }}
                >
                  <FormControl
                    sx={{
                      width: '400px',
                      maxWidth: '100%',
                      marginRight: '10px',
                    }}
                    variant="outlined"
                  >
                    <OutlinedInput
                      placeholder="Référence de la pièce ou du colis"
                      type="text"
                      size="small"
                      disabled
                      sx={{ color: 'black !important' }}
                      value={lineItem.internalProduct}
                      endAdornment={
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="retirer la ligne"
                            onClick={() => handleRemoveItem(lineItem.id)}
                            edge="end"
                          >
                            <CloseIcon />
                          </IconButton>
                        </InputAdornment>
                      }
                    />
                  </FormControl>
                  <FormControl variant="outlined" sx={{ width: '100px' }}>
                    <TextField
                      type="number"
                      label="Quantité"
                      value={lineItem?.quantity || 1}
                      onChange={(e) =>
                        handleChangeQuantity(e.target.value, lineItem.id)
                      }
                      size="small"
                      sx={{ width: '100px' }}
                      inputProps={{
                        min: 1,
                        max: 500,
                        step: 1,
                      }}
                      InputProps={{
                        'aria-label': 'Quantité',
                      }}
                    />
                  </FormControl>
                </Box>
              ))}
            </Box>
          )}

          <Box sx={{ mt: 2 }}>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={isSubmitting || !lines?.length}
              startIcon={
                isSubmitting ? (
                  <CircularProgress size={18} color="inherit" />
                ) : null
              }
            >
              Valider
            </Button>
          </Box>

          {decompositionUi && (
            <Box sx={{ mt: 3 }}>
              {decompositionUi.serverError ? (
                <Alert severity="error">
                  Impossible d&apos;obtenir la vérification de décomposition (réponse vide ou erreur réseau).
                </Alert>
              ) : (
                <>
                  <Typography variant="subtitle1" gutterBottom sx={{ mt: 1 }}>
                    Résultat
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {decompositionUi.summary.errorCount > 0 && (
                      <>Blocages : {decompositionUi.summary.errorCount}. </>
                    )}
                    {decompositionUi.summary.warnCount > 0 && (
                      <>À surveiller : {decompositionUi.summary.warnCount}. </>
                    )}
                    {decompositionUi.summary.successCount > 0 && (
                      <>OK : {decompositionUi.summary.successCount}. </>
                    )}
                  </Typography>

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {decompositionUi.pcsRows.map((row) => (
                      <Alert key={row.reference} severity={row.variant}>
                        <Typography variant="subtitle2" component="div" sx={{ mb: 0.5 }}>
                          {row.reference}
                          {row.candidate ? (
                            <Chip
                              label={`Candidat : ${row.candidate}`}
                              size="small"
                              sx={{ ml: 1, verticalAlign: 'middle' }}
                              color={row.variant === 'error' ? 'default' : 'primary'}
                              variant="outlined"
                            />
                          ) : null}
                        </Typography>
                        {row.successMessage ? (
                          <Typography variant="body2">{row.successMessage}</Typography>
                        ) : null}
                        {row.errorMessage ? (
                          <Typography variant="body2" color="inherit" sx={{ mt: row.successMessage ? 0.5 : 0 }}>
                            {row.errorMessage}
                          </Typography>
                        ) : null}
                        {row.variant === 'info' &&
                        !row.successMessage &&
                        !row.errorMessage &&
                        !row.candidate ? (
                          <Typography variant="body2">
                            Aucune information renvoyée pour cette référence.
                          </Typography>
                        ) : null}
                      </Alert>
                    ))}
                  </Box>

                  {decompositionUi.commonDecompositions.length > 0 ? (
                    <Box sx={{ mt: 3 }}>
                      <Typography variant="subtitle1" gutterBottom>
                        Décompositions communes (même produit interne)
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        {decompositionUi.commonDecompositions.map((group) => (
                          <Alert key={group.internalProductRef} severity="info" icon={false}>
                            <Typography variant="subtitle2" component="div">
                              {group.internalProductRef}{' '}
                              <Chip
                                label={`${group.quantity} unité(s) à décomposer`}
                                size="small"
                                sx={{ ml: 0.5, verticalAlign: 'middle' }}
                              />
                            </Typography>
                            {group.message ? (
                              <Typography variant="body2" sx={{ mt: 0.5 }}>
                                {group.message}
                              </Typography>
                            ) : null}
                            {group?.pcsDetails?.length ? (
                              <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                                {group.pcsDetails.join(' · ')}
                              </Typography>
                            ) : null}
                          </Alert>
                        ))}
                      </Box>
                    </Box>
                  ) : null}
                </>
              )}
            </Box>
          )}
        </Box>
      </Box>
    </Drawer>
  );
};

export default LogisticDrawer;
